export type PeriodPreset = 'today' | '7d' | '30d' | 'month' | 'custom';

export const PERIOD_PRESETS: PeriodPreset[] = ['today', '7d', '30d', 'month', 'custom'];

/// Responsável "sem dono": a fila que ninguém pegou. Não é um id de usuário.
export const UNASSIGNED = 'none';

/// O que a pessoa escolheu no painel. Vazio é "todos".
export interface DashboardFilterState {
    preset: PeriodPreset;
    from: string;
    to: string;
    owner: string;
    source: string;
    campaign: string;
    column: string;
    outcome: string;
}

export const isoDay = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/// "2026-09-28" para 28/09/2026 sem passar por Date (fuso).
export const displayDay = (value: string) => {
    const [year, month, date] = value.slice(0, 10).split('-');
    return `${date}/${month}/${year}`;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/// Por que o período digitado não vale: o campo emite ano pela metade ("0002-10-05").
export function periodError(period: { from: string; to: string }): 'incomplete' | 'reversed' | null {
    const complete = (day: string) => DAY.test(day) && day >= '1900';

    if (!complete(period.from) || !complete(period.to)) return 'incomplete';
    if (period.from > period.to) return 'reversed';

    return null;
}

/// Os dias de cada atalho, contando hoje; "Personalizado" mantém o já escolhido como ponto de partida.
export function presetPeriod(
    preset: PeriodPreset,
    today: Date,
    current?: { from: string; to: string },
): { from: string; to: string } {
    const to = isoDay(today);
    const start = new Date(today);

    switch (preset) {
        case 'today':
            return { from: to, to };
        case '7d':
            start.setDate(today.getDate() - 6);
            return { from: isoDay(start), to };
        case 'month':
            return { from: isoDay(new Date(today.getFullYear(), today.getMonth(), 1)), to };
        case 'custom':
            return current ?? presetPeriod('30d', today);
        default:
            start.setDate(today.getDate() - 29);
            return { from: isoDay(start), to };
    }
}

/// Últimos 30 dias do funil inteiro: responde "como estamos" sem escolha prévia.
export function initialFilters(today = new Date()): DashboardFilterState {
    return {
        preset: '30d',
        ...presetPeriod('30d', today),
        owner: '',
        source: '',
        campaign: '',
        column: '',
        outcome: '',
    };
}

/// Parâmetros da API: só vai o que foi escolhido, pois vazio faria o servidor ler "" como id.
export function filterQuery(filters: DashboardFilterState): Record<string, string> {
    const query: Record<string, string> = { from: filters.from, to: filters.to };

    if (filters.owner === UNASSIGNED) query.unassigned = 'true';
    else if (filters.owner) query.ownerId = filters.owner;

    if (filters.source) query.source = filters.source;
    if (filters.campaign) query.campaign = filters.campaign;
    if (filters.column) query.columnId = filters.column;
    if (filters.outcome) query.outcome = filters.outcome;

    return query;
}

/// Quantos filtros além do período estão ligados.
export function activeFilterCount(filters: DashboardFilterState): number {
    return [filters.owner, filters.source, filters.campaign, filters.column, filters.outcome].filter(Boolean).length;
}

/// Quantos dos "Mais filtros" estão ligados: é o número no botão fechado.
export function extraFilterCount(filters: DashboardFilterState): number {
    return [filters.column, filters.outcome].filter(Boolean).length;
}

export interface Change {
    /// Variação em pontos percentuais; nulo se o período anterior foi zero (0 para 5 não é "+500%").
    percent: number | null;
    direction: 'up' | 'down' | 'flat';
}

export function change(current: number, previous: number): Change {
    if (current === previous) return { percent: 0, direction: 'flat' };

    const direction = current > previous ? 'up' : 'down';

    if (previous === 0) return { percent: null, direction };

    return { percent: Math.round(((current - previous) / previous) * 1000) / 10, direction };
}

/// Ganhos entre os que tiveram fim; nulo sem nenhum encerrado, pois 0% diria que todos foram perdidos.
export function winRate(won: number, lost: number): number | null {
    const closed = won + lost;

    return closed === 0 ? null : Math.round((won / closed) * 1000) / 10;
}
